import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../AuthContext'
import { API_BASE } from '../config'

const T = {
  g900: '#0D2218', g800: '#1A3A28', g700: '#235233', g600: '#2D6B42', g500: '#3D8A57',
  g300: '#85C99A', g100: '#E8F5EE', g50: '#F5FAF7', mute: '#7d8d85', text: '#1A2820', border: '#E3EDE7',
}
const FONT = "'DM Sans',sans-serif"
const SERIF = "'DM Serif Display',serif"

const JOURNEY = [
  ['🩺', 'Consultation', 'Connect with your doctor'],
  ['📋', 'Prescription', 'Get your digital treatment plan'],
  ['🔔', 'Reminders', 'Never miss your dose'],
  ['🤖', 'AI Follow-up', 'Smart check-ins throughout treatment'],
  ['📈', 'Progress Tracking', 'Track progress and recovery'],
]

/**
 * App login: phone number → WhatsApp OTP → verified → straight to Home.
 * Uses /api/otp/send + /api/otp/verify (auto-creates the patient account).
 */
export default function MobileLogin() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const [role] = React.useState('patient') // patient app is patient-only
  const [step, setStep] = React.useState('phone') // 'phone' | 'otp'
  const [phone, setPhone] = React.useState('')
  const [digits, setDigits] = React.useState(['', '', '', '', '', ''])
  const [busy, setBusy] = React.useState(false)
  const [err, setErr] = React.useState('')
  const [devOtp, setDevOtp] = React.useState('') // shown only while WhatsApp token is pending
  const [resendIn, setResendIn] = React.useState(0)
  const boxRefs = React.useRef([])

  React.useEffect(() => {
    if (resendIn <= 0) return
    const id = setInterval(() => setResendIn(s => s - 1), 1000)
    return () => clearInterval(id)
  }, [resendIn > 0])

  const validPhone = /^[6-9]\d{9}$/.test(phone)

  const sendOtp = async () => {
    if (!validPhone || busy) return
    setBusy(true); setErr(''); setDevOtp('')
    try {
      const res = await fetch(`${API_BASE}/api/otp/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ phone, role }),
      })
      const data = await res.json()
      if (!res.ok || !data.sent) throw new Error(data.message || 'Could not send code')
      if (data.otp) setDevOtp(data.otp) // WhatsApp not configured yet → server returns code for testing
      setDigits(['', '', '', '', '', ''])
      setStep('otp'); setResendIn(30)
      setTimeout(() => boxRefs.current[0] && boxRefs.current[0].focus(), 150)
    } catch (e) { setErr(e.message || 'Could not send code') }
    finally { setBusy(false) }
  }

  const verify = async (code) => {
    if (busy) return
    setBusy(true); setErr('')
    try {
      const res = await fetch(`${API_BASE}/api/otp/verify`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ phone, otp: code, role }),
      })
      const data = await res.json()
      if (!res.ok || !data.token) throw new Error(data.message || 'Invalid code')
      const finalRole = data.user.role || (role === 'doctor' ? 'doctor' : 'patient')
      login(finalRole, data.user.name, data.user.email, data.token, data.refreshToken)
      // AuthContext user set → the gate unmounts. Doctors go to their dashboard.
      if (finalRole === 'doctor') navigate('/doctor-dashboard', { replace: true })
    } catch (e) {
      setErr(e.message || 'Invalid code')
      setDigits(['', '', '', '', '', ''])
      setTimeout(() => boxRefs.current[0] && boxRefs.current[0].focus(), 100)
    } finally { setBusy(false) }
  }

  const setDigit = (i, v) => {
    const val = v.replace(/\D/g, '')
    if (!val) { setDigits(d => { const n = [...d]; n[i] = ''; return n }); return }
    // handle paste of the full code into any box
    if (val.length > 1) {
      const chars = val.slice(0, 6).split('')
      const next = ['', '', '', '', '', '']
      chars.forEach((c, j) => { next[j] = c })
      setDigits(next)
      if (chars.length === 6) verify(chars.join(''))
      else boxRefs.current[chars.length] && boxRefs.current[chars.length].focus()
      return
    }
    setDigits(d => {
      const n = [...d]; n[i] = val
      const full = n.join('')
      if (full.length === 6) verify(full)
      return n
    })
    if (i < 5) boxRefs.current[i + 1] && boxRefs.current[i + 1].focus()
  }

  const onKey = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) boxRefs.current[i - 1] && boxRefs.current[i - 1].focus()
  }

  return (
    <div style={{ minHeight: '100vh', background: `linear-gradient(180deg,#FAFDFB, ${T.g50})`, fontFamily: FONT, padding: '2.6rem 1.15rem 2rem', boxSizing: 'border-box' }}>

      {/* Brand */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 46, height: 46, borderRadius: 13, background: `linear-gradient(135deg,${T.g500},${T.g700})`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 6px 16px rgba(61,138,87,.35)' }}>
          <span style={{ fontFamily: SERIF, fontSize: '1.5rem', color: '#fff' }}>R</span>
        </div>
        <div>
          <div style={{ fontFamily: SERIF, fontSize: '1.55rem', color: T.g700, lineHeight: 1 }}>Revive</div>
          <div style={{ fontSize: '.68rem', color: T.mute, marginTop: 3 }}>Continuous Care. Better Outcomes.</div>
        </div>
      </div>

      {/* Headline */}
      <div style={{ marginTop: '1.6rem' }}>
        <div style={{ fontSize: '2rem', fontWeight: 800, color: T.text, lineHeight: 1.15 }}>AI-Powered<br /><span style={{ color: T.g500 }}>Continuous Care</span></div>
        <div style={{ fontSize: '.92rem', color: '#4a5a52', marginTop: '.7rem', lineHeight: 1.55 }}>From Consultation to Recovery,<br />we stay with you every step of the way.</div>
      </div>

      {/* Journey strip */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginTop: '1.5rem' }}>
        {JOURNEY.map(([ic, label, sub], i) => (
          <React.Fragment key={label}>
            <div style={{ textAlign: 'center', width: 64 }}>
              <div style={{ width: 54, height: 54, margin: '0 auto', borderRadius: '50%', background: T.g100, boxShadow: '0 4px 14px rgba(13,34,24,.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem' }}>{ic}</div>
              <div style={{ fontSize: '.62rem', color: T.text, fontWeight: 700, marginTop: 7, lineHeight: 1.25 }}>{label}</div>
              <div style={{ fontSize: '.54rem', color: T.mute, marginTop: 3, lineHeight: 1.35 }}>{sub}</div>
            </div>
            {i < JOURNEY.length - 1 && <div style={{ flex: 1, marginTop: 26, textAlign: 'center', color: '#b9cdc2', fontSize: '.6rem', letterSpacing: 2 }}>···</div>}
          </React.Fragment>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: '1.2rem', background: T.g100, borderRadius: 999, padding: '.65rem 1rem', fontSize: '.74rem', color: '#3a4a42' }}>
        <span style={{ color: T.g500, fontSize: '.9rem' }}>♡</span> Your treatment journey, connected from consultation to recovery.
      </div>

      {/* Login card */}
      <div style={{ marginTop: '1.3rem', background: '#fff', borderRadius: 24, boxShadow: '0 12px 36px rgba(13,34,24,.08)', border: `1px solid ${T.border}`, padding: '1.5rem 1.3rem' }}>
        {step === 'phone' ? (
          <>
            {/* Patient app is patient-only — doctors use the separate Revive Doctor app. */}
            <div style={{ width: 52, height: 52, borderRadius: '50%', background: T.g100, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', marginBottom: '1rem' }}>📞</div>
            <div style={{ fontSize: '1.3rem', fontWeight: 800, color: T.text }}>{role === 'doctor' ? <>Doctor <span style={{ color: T.g600 }}>sign in</span></> : <>Continue with your <span style={{ color: T.g600 }}>phone number</span></>}</div>
            <div style={{ fontSize: '.85rem', color: T.mute, margin: '.4rem 0 1.2rem' }}>{role === 'doctor' ? 'Enter your registered doctor number for a WhatsApp code.' : "We'll send a verification code via WhatsApp."}</div>

            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6, border: `1.5px solid ${T.border}`, borderRadius: 14, padding: '.85rem .9rem', fontWeight: 700, color: T.text, whiteSpace: 'nowrap' }}>+91 <span style={{ color: T.mute, fontSize: '.7rem' }}>▾</span></div>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', border: `1.5px solid ${err ? '#C0392B' : T.border}`, borderRadius: 14, padding: '.85rem 1rem' }}>
                <input value={phone} onChange={e => { setPhone(e.target.value.replace(/\D/g, '').slice(0, 10)); setErr('') }}
                  onKeyDown={e => e.key === 'Enter' && sendOtp()}
                  type="tel" inputMode="numeric" placeholder="Enter your mobile number" autoFocus
                  style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', fontSize: '.98rem', fontFamily: FONT, color: T.text }} />
              </div>
            </div>

            <button onClick={sendOtp} disabled={!validPhone || busy}
              style={{ width: '100%', marginTop: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, background: validPhone ? `linear-gradient(135deg,${T.g500},${T.g700})` : '#CBDCD2', color: '#fff', border: 'none', borderRadius: 14, padding: '1rem', fontWeight: 800, fontSize: '1.02rem', cursor: validPhone ? 'pointer' : 'default', fontFamily: FONT }}>
              {busy ? 'Sending…' : <>Continue <span>→</span></>}
            </button>

            {err && <div style={{ fontSize: '.78rem', color: '#C0392B', marginTop: '.7rem' }}>⚠ {err}</div>}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: '1.1rem', fontSize: '.78rem', color: '#4a5a52' }}>
              <span style={{ width: 26, height: 26, borderRadius: 8, background: T.g100, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '.8rem' }}>🔒</span>
              Secure login via WhatsApp
            </div>
          </>
        ) : (
          <>
            <div style={{ width: 52, height: 52, borderRadius: '50%', background: T.g100, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', marginBottom: '1rem' }}>💬</div>
            <div style={{ fontSize: '1.3rem', fontWeight: 800, color: T.text }}>Enter <span style={{ color: T.g600 }}>verification code</span></div>
            <div style={{ fontSize: '.85rem', color: T.mute, margin: '.4rem 0 1.2rem' }}>
              Sent via WhatsApp to <strong style={{ color: T.text }}>+91 {phone}</strong>
              <span onClick={() => { setStep('phone'); setErr('') }} style={{ color: T.g700, fontWeight: 700, marginLeft: 8, cursor: 'pointer' }}>Edit</span>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
              {digits.map((d, i) => (
                <input key={i} ref={el => (boxRefs.current[i] = el)} value={d}
                  onChange={e => setDigit(i, e.target.value)} onKeyDown={e => onKey(i, e)}
                  type="tel" inputMode="numeric" autoComplete={i === 0 ? 'one-time-code' : 'off'} maxLength={6}
                  style={{ width: '15%', aspectRatio: '1', textAlign: 'center', fontSize: '1.35rem', fontWeight: 800, color: T.text, border: `1.5px solid ${err ? '#C0392B' : d ? T.g500 : T.border}`, borderRadius: 12, outline: 'none', fontFamily: FONT, background: d ? T.g50 : '#fff', boxSizing: 'border-box' }} />
              ))}
            </div>

            {devOtp && <div style={{ marginTop: '.9rem', fontSize: '.78rem', background: '#FFF8E1', border: '1px solid #FFE082', color: '#8D6E00', borderRadius: 10, padding: '.55rem .8rem' }}>🧪 Test mode (WhatsApp pending): your code is <strong>{devOtp}</strong></div>}
            {err && <div style={{ fontSize: '.78rem', color: '#C0392B', marginTop: '.7rem' }}>⚠ {err}</div>}
            {busy && <div style={{ fontSize: '.8rem', color: T.mute, marginTop: '.7rem' }}>Verifying…</div>}

            <div style={{ marginTop: '1.1rem', fontSize: '.8rem', color: T.mute, textAlign: 'center' }}>
              {resendIn > 0 ? <>Resend code in <strong style={{ color: T.text }}>{resendIn}s</strong></> : <span onClick={sendOtp} style={{ color: T.g700, fontWeight: 700, cursor: 'pointer' }}>↻ Resend code</span>}
            </div>
          </>
        )}
      </div>

      <div style={{ textAlign: 'center', fontSize: '.72rem', color: T.mute, marginTop: '1.1rem', lineHeight: 1.6 }}>
        By continuing, you agree to our <a href="/terms-of-service" style={{ color: T.g700 }}>Terms & Conditions</a><br />and <a href="/privacy-policy" style={{ color: T.g700 }}>Privacy Policy</a>.
      </div>
    </div>
  )
}
